import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Edit } from 'lucide-react';

import { $renameFile } from '../../_state/fileActions.state';
import {
  $selectedFileForAction,
  $showRenameDialog,
} from '../../_state/fileDialogs.state';
import { $files } from '../../_state/files.state';
import { useTranslations } from '../../i18n/useTranslations';

export const FileRenameDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const [isOpen, setIsOpen] = useAtom($showRenameDialog);
  const selectedFileId = useAtomValue($selectedFileForAction);
  const files = useAtomValue($files);
  const renameFile = useSetAtom($renameFile);

  const selectedFile = files.find((f) => f.id === selectedFileId);

  const [newName, setNewName] = React.useState('');
  const [isRenaming, setIsRenaming] = React.useState(false);

  // Update newName when dialog opens with a new file
  React.useEffect(() => {
    if (isOpen && selectedFile) {
      setNewName(selectedFile.name);
    }
  }, [isOpen, selectedFile]);

  const handleRename = async () => {
    if (!newName.trim()) {
      toast.error(t.dialogs.rename.errorNoName);
      return;
    }

    if (!selectedFileId) {
      toast.error(t.dialogs.rename.errorNoFile);
      return;
    }

    if (newName.trim() === selectedFile?.name) {
      // No change, just close
      setIsOpen(false);
      return;
    }

    setIsRenaming(true);

    try {
      await renameFile({ fileId: selectedFileId, newName: newName.trim() });
      toast.success(t.dialogs.rename.successRenamed.replace('{name}', newName));
      setIsOpen(false);
      setNewName('');
    } catch (error) {
      toast.error(
        t.dialogs.rename.errorFailed.replace(
          '{error}',
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsRenaming(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setNewName('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isRenaming && newName.trim()) {
      handleRename();
    }
  };

  if (!selectedFile) {
    return null;
  }

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className="@FileRenameDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Edit className="size-5" />
            {selectedFile.type === 'Directory'
              ? t.dialogs.rename.titleFolder
              : t.dialogs.rename.titleFile}
          </DialogTitle>
        </DialogHeader>

        <div className="p-4">
          <TextField
            label={t.dialogs.rename.label}
            placeholder={t.dialogs.rename.placeholder}
            value={newName}
            onChange={setNewName}
            onKeyDown={handleKeyDown}
            autoFocus
            isRequired
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onPress={handleClose}
            isDisabled={isRenaming}
          >
            {t.dialogs.rename.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={handleRename}
            isDisabled={!newName.trim() || isRenaming}
            isLoading={isRenaming}
          >
            {t.dialogs.rename.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
